import { EJobRecordStatus, EJobStatus, IAccessLevel, type IJobRecord } from '@quesmed/types/models';
import { type Key, useMemo } from 'react';
import { BiDotsHorizontal, BiDuplicate } from 'react-icons/bi';
import { BsCopy } from 'react-icons/bs';
import { FaCheckCircle, FaEdit, FaRocket, FaTimesCircle } from 'react-icons/fa';
import { FaBoxOpen, FaEye, FaTrash } from 'react-icons/fa6';
import { FiUnlock } from 'react-icons/fi';
import { GrTest } from 'react-icons/gr';
import { EFormMode } from '@/config/globals';
import { iconClasses } from '@/config/job';
import { ActionDropdown, cn, toaster } from '@/core/ui';
import { authState } from '@/store/auth';
import { useJob } from '@/store/job';
import { useRecord } from '@/store/record';

export const RecordAction = {
  VIEW: {
    key: 'view',
    className: 'text-xs py-1',
    startContent: <FaEye className={cn(iconClasses)} />,
    title: 'View',
  },
  EDIT: {
    key: 'edit',
    className: 'text-xs py-1',
    startContent: <FaEdit className={cn(iconClasses)} />,
    title: 'Edit',
  },
  DUPLICATE: {
    key: 'duplicate',
    className: 'text-xs py-1',
    startContent: <BiDuplicate className={cn(iconClasses)} />,
    title: 'Duplicate',
  },
  APPROVE: {
    key: 'approve',
    className: 'text-xs py-1',
    startContent: <FaCheckCircle className={cn(iconClasses, 'text-success-300')} />,
    title: 'Approve',
    color: 'success',
  },
  REJECT: {
    key: 'reject',
    className: 'text-xs py-1',
    startContent: <FaTimesCircle className={cn(iconClasses, 'text-danger-300')} />,
    title: 'Reject',
    color: 'danger',
  },
  PUBLISH: {
    key: 'publish',
    className: 'text-xs py-1',
    startContent: <FaRocket className={cn(iconClasses, 'text-secondary-300')} />,
    title: 'Publish',
    color: 'secondary',
  },
  TESTING: {
    key: 'testing',
    className: 'text-xs py-1',
    startContent: <GrTest className={cn(iconClasses, '')} />,
    title: 'Testing',
    color: 'default',
  },
  READY: {
    key: 'ready',
    className: 'text-xs py-1',
    startContent: <FaBoxOpen className={cn(iconClasses, 'text-secondary-300')} />,
    title: 'Mark Ready',
    showDivider: false,
    color: 'secondary',
    // description:
    //   'Mark this record as ready. This lets editors know it is ready for review, or serves as a personal marker that the record is complete.',
  },
  UNLOCK: {
    key: 'unlock',
    className: 'text-xs py-1',
    startContent: <FiUnlock className={cn(iconClasses, 'text-warning-300')} />,
    title: 'Unlock',
    color: 'warning',
    // description: 'Unlock this record. This lets writers to edit this record again',
  },
  DELETE: {
    key: 'delete',
    className: 'text-xs py-1',
    startContent: <FaTrash className={cn(iconClasses, 'text-danger-300')} />,
    title: 'Delete',
    color: 'danger',
  },
};

const statusActions: Record<
  Exclude<EJobRecordStatus, EJobRecordStatus.ALL>,
  (typeof RecordAction)[keyof typeof RecordAction][]
> = {
  [EJobRecordStatus.DRAFT]: [
    RecordAction.VIEW,
    RecordAction.EDIT,
    { ...RecordAction.DUPLICATE, showDivider: true },
    RecordAction.READY,
    RecordAction.DELETE,
  ],
  [EJobRecordStatus.READY]: [
    RecordAction.VIEW,
    { ...RecordAction.EDIT, showDivider: true },
    { ...RecordAction.DUPLICATE, showDivider: true },
    RecordAction.APPROVE,
    RecordAction.REJECT,
    RecordAction.DELETE,
  ],
  [EJobRecordStatus.APPROVED]: [
    RecordAction.VIEW,
    // RecordAction.EDIT,
    RecordAction.DUPLICATE,
    RecordAction.REJECT,
    RecordAction.TESTING,
    RecordAction.DELETE,
  ],
  [EJobRecordStatus.REJECTED]: [
    RecordAction.VIEW,
    RecordAction.EDIT,
    RecordAction.DUPLICATE,
    RecordAction.APPROVE,
    // RecordAction.TESTING,
    RecordAction.DELETE,
  ],
  [EJobRecordStatus.TESTING]: [
    RecordAction.VIEW,
    { ...RecordAction.DUPLICATE, showDivider: true },
    RecordAction.UNLOCK,
    RecordAction.PUBLISH,
    RecordAction.DELETE,
  ],
  [EJobRecordStatus.PUBLISHED]: [
    RecordAction.VIEW,
    { ...RecordAction.DUPLICATE, showDivider: true },
    RecordAction.UNLOCK,
    RecordAction.DELETE,
  ],
  [EJobRecordStatus.PAID]: [RecordAction.VIEW, { ...RecordAction.DUPLICATE, showDivider: true }],
};

export const RecordItemActions = ({ record }: { record: IJobRecord }) => {
  const accessLevel = authState((s) => s.accessLevel);
  const setRecordModal = useRecord.use.setRecordModal();
  const setRecordId = useRecord.use.setRecordId();
  const setConfirmModal = useRecord.use.setConfirmModal();
  const setCurrentRecord = useRecord.use.setCurrentRecord();
  const currentJob = useJob.use.currentJob();
  const handleAction = (key: Key) => {
    switch (key) {
      case 'view': {
        setCurrentRecord(record);
        setRecordId(record.id);
        setRecordModal(true, EFormMode.VIEW);
        break;
      }
      case 'edit': {
        setCurrentRecord(record);
        setRecordId(record.id);
        setRecordModal(true, EFormMode.EDIT);
        break;
      }
      case 'duplicate': {
        setConfirmModal(
          true,
          'Duplicate Job',
          <>
            Are you sure want to duplicate this record with Id <b>{record.id}</b>?
          </>,
          'duplicate',
          record.id,
        );
        break;
      }
      case 'ready': {
        setConfirmModal(
          true,
          'Mark Ready',
          <>
            Are you sure want to mark this record as ready with Id <b>{record.id}</b>?
          </>,
          'ready',
          record.id,
        );
        break;
      }
      case 'approve': {
        setConfirmModal(
          true,
          'Approve Record',
          <>
            Are you sure want to approve this record with Id <b>{record.id}</b>?
          </>,
          'approve',
          record.id,
        );
        break;
      }
      case 'reject': {
        setConfirmModal(
          true,
          'Reject Record',
          <>
            Are you sure want to reject this record with Id <b>{record.id}</b>?
          </>,
          'reject',
          record.id,
        );
        break;
      }
      case 'unlock': {
        setConfirmModal(
          true,
          'Unlock Record',
          <>
            Are you sure want to unlock this record with Id <b>{record.id}</b>? It will be available for
            editing by writers.
          </>,
          'unlock',
          record.id,
        );
        break;
      }
      case 'publish': {
        setConfirmModal(
          true,
          'Publish Record',
          <>
            Are you sure want to publish this record with Id <b>{record.id}</b>? It will be saved to database
            with record data and available for users in quiz in real time.
          </>,
          'publish',
          record.id,
        );
        break;
      }
      case 'testing': {
        setConfirmModal(
          true,
          'Mark as Testing',
          <>
            Are you sure want to mark this record as testing with Id <b>{record.id}</b>? It will be saved to
            database with record data and available for users in quiz in real time.
          </>,
          'testing',
          record.id,
        );
        break;
      }
      case 'delete': {
        setConfirmModal(
          true,
          'Delete Job',
          <div className='text-sm flex flex-col gap-2'>
            <span>
              Are you sure want to delete this record with Id <b>{record.id}</b>?
            </span>
            {record.assets.length > 0 && (
              <span className='text-default-500'>
                This will delete the {record.assets.length} assets associated with this record.
              </span>
            )}
          </div>,
          'delete',
          record.id,
        );
        break;
      }
      case 'copy': {
        navigator.clipboard.writeText(record.id);
        toaster.success('Record ID copied to clipboard');
        break;
      }
      default:
        break;
    }
  };

  // const disabledKeys = useMemo(() => {
  //   // const jobStatus = currentJob?.status;
  //   // const editableByWriter =
  //   //   jobStatus && [EJobStatus.IDLE, EJobStatus.ONGOING, EJobStatus.REJECTED].includes(jobStatus);
  //   // const disabled = jobStatus && [EJobStatus.TESTING, EJobStatus.PUBLISHED].includes(jobStatus);

  //   // switch (record.status) {
  //   //   case EJobRecordStatus.DRAFT: {
  //   //     return disabled ? ['edit'] : ['delete', 'edit'];
  //   //   }
  //   //   case EJobRecordStatus.READY: {
  //   //     return disabled ? ['edit'] : ['delete'];
  //   //   }
  //   //   case EJobRecordStatus.APPROVED: {
  //   //     return disabled ? ['edit'] : ['delete', 'edit'];
  //   //   }
  //   //   case EJobRecordStatus.REJECTED: {
  //   //     return disabled ? ['edit'] : ['delete', 'edit'];
  //   //   }
  //   //   case EJobRecordStatus.TESTING: {
  //   //     return ['delete', 'edit'];
  //   //   }
  //   //   case EJobRecordStatus.PUBLISHED: {
  //   //     return ['delete', 'edit'];
  //   //   }
  //   //   default: {
  //   //     return [];
  //   //   }
  //   // }

  //   // if (record.status && record.status >= EJobRecordStatus.TESTING) {
  //   //   return ['delete', 'edit'];
  //   // }

  //   switch (accessLevel) {
  //     case IAccessLevel.WRITER: {
  //       if (
  //         currentJob?.status &&
  //         ![EJobStatus.IDLE, EJobStatus.ONGOING, EJobStatus.REJECTED].includes(currentJob.status)
  //       )
  //         return ['edit', 'duplicate', 'delete', 'ready'];
  //       return [];
  //     }
  //     case IAccessLevel.EDITOR: {
  //       return ['duplicate', 'delete', 'ready'];
  //     }
  //     default: {
  //       if (currentJob?.status && currentJob.status >= EJobStatus.TESTING) return ['duplicate', 'delete'];
  //       return [];
  //     }
  //   }
  // }, [accessLevel, currentJob, record.status]);

  const items = useMemo(() => {
    if (currentJob?.trial) {
      return [RecordAction.VIEW];
    }

    const isFull = currentJob?.recordCount === currentJob?.totalRecords;
    const isLocked = currentJob?.status && currentJob.status >= EJobStatus.TESTING;
    const isOpenForWriter =
      currentJob?.status &&
      [EJobStatus.ONGOING, EJobStatus.REJECTED, EJobStatus.IDLE].includes(currentJob.status);
    let actions = statusActions[record.status as Exclude<EJobRecordStatus, EJobRecordStatus.ALL>] || [];

    if (accessLevel === IAccessLevel.WRITER) {
      actions = actions.filter((a) => !['unlock', 'approve', 'reject', 'publish', 'testing'].includes(a.key));
      if (!isOpenForWriter) {
        actions = actions.filter((a) => !['edit', 'delete'].includes(a.key));
      }
    }
    if (isFull) {
      actions = actions.filter((action) => !['duplicate'].includes(action.key));
    }
    if (isLocked) {
      actions = actions.filter((a) => !['delete'].includes(a.key));
    }
    return actions;
  }, [record.status, accessLevel, currentJob]);

  return (
    <ActionDropdown
      offset={0}
      classNames={{
        content: 'shadow-sm border border-default-200 rounded-md min-w-[130px]',
      }}
      onAction={handleAction}
      // disabledKeys={disabledKeys}
      items={[
        {
          key: 'copy',
          className: 'text-xs py-1',
          startContent: <BsCopy className={cn(iconClasses, '')} />,
          title: 'Copy',
          color: 'default',
        },
        ...items,
      ]}
    >
      <div className='hover:bg-content2 w-8 h-6 flex justify-center items-center rounded-md px-1 py-0.5 cursor-pointer text-default-400'>
        <BiDotsHorizontal className='w-5 h-auto cursor-pointer text-default-400' />
      </div>
    </ActionDropdown>
  );
};
